import {
  Box,
  Button,
  FormControl,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { HiOutlineUsers, HiOutlineLockClosed } from "react-icons/hi";
import { publishMyReadingOrder } from "../../../services/readingOrdersApi";
import { trackEvent } from "../../../shared/analytics";

const PublishMyOrderModal = ({ isOpen, onClose, readingOrder }) => {
  const queryClient = useQueryClient();
  const [selectedAction, setSelectedAction] = useState(1);

  const publishMutation = useMutation(
    ({ readingOrderId, privacyMode }) => {
      return publishMyReadingOrder(readingOrderId, privacyMode);
    },
    {
      onSuccess: async (response, variables) => {
        queryClient.invalidateQueries(["myReadingOrders"]);
        return await queryClient.refetchQueries({
          queryKey: ["readingOrder"],
          exact: false,
        });
      },
    }
  );

  const onSubmit = () => {
    publishMutation
      .mutateAsync({
        readingOrderId: readingOrder?.id,
        privacyMode: parseInt(selectedAction, 10),
      })
      .then(() => {
        trackEvent("my_order_published", {
          reading_order_id: readingOrder?.id,
          privacy_mode: parseInt(selectedAction, 10),
          first_publish: !readingOrder?.published,
        });
        onClose();
      });
  };

  return (
    <Modal onClose={onClose} isOpen={isOpen} isCentered size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {readingOrder?.published
            ? "Set visibility of my reading order"
            : "Publish my order"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <RadioGroup
                value={parseInt(selectedAction, 10)}
                onChange={(action) => setSelectedAction(action)}
              >
                <VStack alignItems="flex-start" spacing={6}>
                  <Box>
                    <Radio value={1} tabIndex={1}>
                      <Box display="flex" alignItems="center">
                        <Text as="b">Share with Redwing community</Text>
                        &nbsp;
                        <HiOutlineUsers />
                      </Box>
                    </Radio>
                    <List pt="2" spacing={1}>
                      <ListItem fontSize="sm" textColor="gray.300" pl="4">
                        📢 Other users can read and enjoy your reading order.
                      </ListItem>
                      <ListItem fontSize="sm" textColor="gray.300" pl="4">
                        🔄 Any updates you make will be reflected for all
                        readers.
                      </ListItem>
                      <ListItem fontSize="sm" textColor="gray.300" pl="4">
                        🔒 If you make it private later, users who added it to
                        their reading list will still have access but it will
                        disappear from the community section.
                      </ListItem>
                    </List>
                  </Box>

                  <Box>
                    <Radio value={0} tabIndex={2}>
                      <Box display="flex" alignItems="center">
                        <Text as="b">Keep it private</Text>
                        &nbsp;
                        <HiOutlineLockClosed />
                      </Box>
                    </Radio>
                    <List pt="2" spacing={1}>
                      <ListItem fontSize="sm" textColor="gray.300" pl="4">
                        👁️ Only you can view your reading order.
                      </ListItem>
                      <ListItem fontSize="sm" textColor="gray.300" pl="4">
                        ✏️ You can always edit it at any time.
                      </ListItem>
                      <ListItem fontSize="sm" textColor="gray.300" pl="4">
                        🔓 You can always share it later.
                      </ListItem>
                    </List>
                  </Box>
                </VStack>
              </RadioGroup>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button tabIndex={6} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            color="white"
            tabIndex={5}
            ml="3"
            onClick={onSubmit}
            disabled={publishMutation.isLoading}
          >
            {readingOrder?.published
              ? parseInt(selectedAction, 10) === 1
                ? `Share reading order`
                : `Set to private`
              : `Publish ${
                  parseInt(selectedAction, 10) === 1 ? "and share" : "privately"
                }`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PublishMyOrderModal;
